export function buildServiceUrls(
    env: Window["AppEnv"] | undefined,
    loc: Pick<Location, "protocol" | "hostname">,
): { WS_HOST: string; API_BASE_URL: string; ADMIN_PANEL_URL: string } {
    const port = (key: "WS_PORT" | "API_PORT") => {
        const raw = env?.[key];
        if (raw == null || raw === "") return "";
        return String(raw).trim();
    };
    const hostname = loc.hostname;
    const host = hostname.includes(":") ? `[${hostname}]` : hostname;
    const scheme = loc.protocol === "https:" ? "https" : "http";
    const origin = (value: string) => {
        if (!value) return "";
        const defaultPort = scheme === "https" ? "443" : "80";
        const portPart = value === defaultPort ? "" : `:${value}`;
        return `${scheme}://${host}${portPart}`;
    };
    const ws = origin(port("WS_PORT"));
    const api = origin(port("API_PORT"));
    return {
        WS_HOST: ws,
        API_BASE_URL: api ? `${api}/api/v1/` : "",
        // Flask-Admin lives on the API origin at /admin/, not under /api/v1/.
        ADMIN_PANEL_URL: api ? `${api}/admin/` : "",
    };
}

const serviceUrls = buildServiceUrls(window.AppEnv, window.location);
export const WS_HOST = serviceUrls.WS_HOST;
export const API_BASE_URL = serviceUrls.API_BASE_URL;
export const ADMIN_PANEL_URL = serviceUrls.ADMIN_PANEL_URL;

export const MIN_DATE = "2021-06-01";

export enum OperationEnum {
    ENTRY = 1,
    TRANSFER = 2,
    INVENTORY = 3,
    DISCARD = 4,
    CONVERSION = 5,
    PRODUCTION = 6
}

export enum DepotEnum {
    EXTERNAL_IN = 1,
    SALES = 2,
    WASTE = 3,
}

/** Reserved payment-mode IDs. Wallet and voucher are system client tenders. */
export enum PayModeEnum {
    CASH = 1,
    DISCOUNT = 2,
    WALLET = 3,
    VOUCHER = 4,
}

export const SYSTEM_PAY_MODE_IDS = [
    PayModeEnum.CASH,
    PayModeEnum.DISCOUNT,
    PayModeEnum.WALLET,
    PayModeEnum.VOUCHER,
] as const;

export const isWalletPayMode = (modeId: number | string | null | undefined): boolean =>
    Number(modeId) === PayModeEnum.WALLET;

export const isVoucherPayMode = (modeId: number | string | null | undefined): boolean =>
    Number(modeId) === PayModeEnum.VOUCHER;

export const isDiscountPayMode = (modeId: number | string | null | undefined): boolean =>
    Number(modeId) === PayModeEnum.DISCOUNT;

export const isChangeTenderMode = (modeId: number | string | null | undefined): boolean =>
    !isDiscountPayMode(modeId) && !isWalletPayMode(modeId) && !isVoucherPayMode(modeId);

// Same rounding as roundMoney in quantity.ts (half away from zero, no binary drift:
// 10 - 9.9 is 0.0999...). Duplicated because quantity.ts imports this module.
const roundCents = (value: number): number => {
    if (!Number.isFinite(value)) return 0;
    const abs = Math.abs(value);
    const text = String(abs);
    const rounded = text.includes('e')
        ? Math.round(abs * 100) / 100
        : Number(`${Math.round(Number(`${text}e2`))}e-2`);
    return value < 0 && rounded !== 0 ? -rounded : rounded;
};

export const paymentChangeAmount = (payment?: { amount?: number; received?: number } | null) => {
    const amount = Number(payment?.amount || 0);
    const received = Number(payment?.received || 0);
    return received > amount ? roundCents(received - amount) : 0;
};

export const seedPaymentReceived = (payment?: { amount?: number; received?: number } | null) => {
    const amount = Math.max(0, Number(payment?.amount || 0));
    const received = Math.max(0, Number(payment?.received || 0));
    return received > 0 ? Math.max(received, amount) : amount;
};

export const parseReceivedInput = (raw: string) => {
    const value = String(raw ?? '').trim();
    if (value === '' || value === '-') return 0;
    const next = Number(value);
    if (!Number.isFinite(next)) return 0;
    return Math.max(0, roundCents(next));
};

export const receivedBelowAmount = (payment?: { amount?: number; received?: number } | null) =>
    Number(payment?.received || 0) < Number(payment?.amount || 0);

const isDiscountModeName = (name?: string | null): boolean =>
    String(name || '').trim().toLowerCase() === 'discount';

export const excludeWalletPayModes = <T extends { id: number | string }>(
    modes: readonly T[],
): T[] => modes.filter((mode) => !isWalletPayMode(mode.id) && !isVoucherPayMode(mode.id));

export const excludeExpensePayModes = <T extends { id: number | string; mode_name?: string }>(
    modes: readonly T[],
): T[] =>
    modes.filter(
        (mode) =>
            !isWalletPayMode(mode.id) &&
            !isVoucherPayMode(mode.id) &&
            !isDiscountPayMode(mode.id) &&
            !isDiscountModeName(mode.mode_name),
    );

export enum LocalStorageKeys {
    MOVEMENT_TASKS = 'pending_movement_tasks',
    MOVEMENT_BATCHED_RECORDS = 'movement_batched_records',
    MOVEMENT_LIST_RECORDS = 'movement_list_records',
    MOVEMENT_TRACE_RECORDS = 'movement_trace_records',
    MOVEMENT_TRACE_CHRONO_RECORDS = 'movement_trace_chrono_records',

    SUPPLY_TASKS = 'pending_supply_tasks',
    SUPPLY_BATCHED_RECORDS = 'supply_batched_records',
    SUPPLY_LIST_RECORDS = 'supply_list_records',

    SALE_TASKS = 'pending_sale_tasks',
    POS_LAYOUT_MODE = 'pos_layout_mode',
    POS_CART_VIEW_MODE = 'pos_cart_view_mode',
    APP_PRINTER = 'app_printer',
    SYSTEM_PRINTER = 'system_printer',
    SALES_RECORDS = 'sales_records',
    SALES_SECTOR_RECORDS = 'sales_sector_records',
    SALES_FAMILY_RECORDS = 'sales_family_records',
    SALES_CATEGORY_RECORDS = 'sales_category_records',
    SALES_DEPOT_RECORDS = 'sales_depot_records',
    SALES_USER_RECORDS = 'sales_user_records',
    SALES_ITEM_RECORDS = 'sales_item_records',

    CONVERSION_TASKS = 'pending_conversion_task',
    PRODUCTION_TASKS = 'pending_production_task',
    PRODUCTION_RECORDS = 'production_records',
    PRODUCTION_BOM_RECORDS = 'production_bom_records',
    BOM_TASKS = 'pending_bom_task',
    
    INVSIT_BATCHED_RECORDS = 'invsit_batched_records',
    CLIENT_SIT_RECORDS = 'client_situation_records',
    WALLET_RECORDS = 'wallet_records',
    WALLET_LIST = 'wallet_list',
    EXPENSE_RECORDS = 'expense_records',
    VOUCHER_RECORDS = 'voucher_records',
    OFFER_RECORDS = 'offer_records',
    OFFER_TASKS = 'pending_offer_tasks',
    AGENT_SIT_RECORDS = 'agent_situation_records',
    SUPPLIER_SIT_RECORDS = 'supplier_situation_records',
    SUPPLY_SIT_BATCHED_RECORDS = 'supply_situation_batched_records',

    PAYMENT_RECORDS = 'payment_records',
    SUPPLY_PAYMENT_RECORDS = 'supply_payment_records',
    SUPPLY_PAYMENT_MODE_RECORDS = 'supply_payment_mode_records',
    SUPPLY_PAYMENT_USER_RECORDS = 'supply_payment_user_records',
    SUPPLY_PAYMENT_DEPOT_RECORDS = 'supply_payment_depot_records',
    PAYMENT_MODE_RECORDS = 'payment_mode_records',
    PAYMENT_TYPE_RECORDS = 'payment_type_records',
    PAYMENT_USER_RECORDS = 'payment_user_records',
    PAYMENT_DEPOT_RECORDS = 'payment_depot_records',

    DEPOSIT_RECORDS = 'deposit_records',
    PENDING_DEPOSIT_RECORDS = 'pending_deposit_records',
    PENDING_DEPOSIT_ITEMS = 'pending_deposit_items',

    INVENTORY_BATCHED_RECORDS = 'inventory_batched_records',
    INVENTORY_TASKS = 'pending_inventory_tasks',
    CURRENT_INVENTORY = 'current_inventory',
    CURRENT_INVENTORY_VISUAL = 'current_inventory_visual',
    // MOVEMENT_TASK_ID = 'movement_task_id',
    // MOVEMENT_TASK_BUNDLE = 'movement_task_bundle',
    // MOVEMENT_TASK_BUNDLE_ITEMS = 'movement_task_bundle_items',
    // MOVEMENT_TASK_BUNDLE_ITEM = 'movement_task_bundle_item',
    // MOVEMENT_TASK_BUNDLE_ITEM_ID = 'movement_task_bundle_item_id',
    // MOVEMENT_TASK_BUNDLE_ITEM_NAME = 'movement_task_bundle_item_name',
}


export const INVENTORY_TYPES = [
    { key: "EOD", label: "End-of-Day" },
    { key: "IP", label: "In-place" },
    { key: "SOD", label: "Start-of-Day" },
];


export const MYSQL_LIMITS = {
    TINYINT: { min: -128, max: 127, unsignedMax: 255 },
    SMALLINT: { min: -32768, max: 32767, unsignedMax: 65535 },
    MEDIUMINT: { min: -8388608, max: 8388607, unsignedMax: 16777215 },
    INT: { min: -2147483648, max: 2147483647, unsignedMax: 4294967295 },
    BIGINT: { min: -9223372036854775808n, max: 9223372036854775807n, unsignedMax: 18446744073709551615n },

    // strings
    CHAR: (n: number) => ({ maxLength: n }),
    VARCHAR: (n: number) => ({ maxLength: n }),
    TEXT: { maxLength: 65535 },
    MEDIUMTEXT: { maxLength: 16777215 },
    LONGTEXT: { maxLength: 4294967295 },

    // decimals (you pass precision + scale)
    DECIMAL: (precision: number, scale: number) => ({
        min: -(10 ** (precision - scale)) + (1 / (10 ** scale)),
        max: (10 ** (precision - scale)) - (1 / (10 ** scale)),
        scale,
    }),

    // dates
    DATE: { format: "YYYY-MM-DD" },
    DATETIME: { format: "YYYY-MM-DD HH:mm:ss" },
    TIMESTAMP: { format: "YYYY-MM-DD HH:mm:ss" },
};


export const LOCAL_STORAGE_KEY_LABELS: Record<string, string> = {
    [LocalStorageKeys.MOVEMENT_TASKS]: "Pending Movement Tasks",
    [LocalStorageKeys.MOVEMENT_BATCHED_RECORDS]: "Movement Batched Records",
    [LocalStorageKeys.MOVEMENT_LIST_RECORDS]: "Movement List Records",
    [LocalStorageKeys.MOVEMENT_TRACE_RECORDS]: "Movement Trace Records",
    [LocalStorageKeys.MOVEMENT_TRACE_CHRONO_RECORDS]: "Movement Trace (Chronological)",

    [LocalStorageKeys.SUPPLY_TASKS]: "Pending Supply Tasks",
    [LocalStorageKeys.SUPPLY_BATCHED_RECORDS]: "Supply Batched Records",
    [LocalStorageKeys.SUPPLY_LIST_RECORDS]: "Supply List Records",

    [LocalStorageKeys.SALE_TASKS]: "Pending Sale Tasks",
    [LocalStorageKeys.SALES_RECORDS]: "Sales Records",
    [LocalStorageKeys.SALES_SECTOR_RECORDS]: "Sales by Sector",
    [LocalStorageKeys.SALES_FAMILY_RECORDS]: "Sales by Family",
    [LocalStorageKeys.SALES_CATEGORY_RECORDS]: "Sales by Category",
    [LocalStorageKeys.SALES_DEPOT_RECORDS]: "Sales by Depot",
    [LocalStorageKeys.SALES_USER_RECORDS]: "Sales by User",
    [LocalStorageKeys.SALES_ITEM_RECORDS]: "Sales by Item",

    [LocalStorageKeys.CONVERSION_TASKS]: "Pending Conversion Tasks",
    [LocalStorageKeys.PRODUCTION_TASKS]: "Pending Production Tasks",
    [LocalStorageKeys.PRODUCTION_RECORDS]: "Production Records",
    [LocalStorageKeys.PRODUCTION_BOM_RECORDS]: "Production BOMs",
    [LocalStorageKeys.BOM_TASKS]: "Pending BOM Tasks",

    [LocalStorageKeys.INVSIT_BATCHED_RECORDS]: "Inventory Situation Batched Records",
    [LocalStorageKeys.CLIENT_SIT_RECORDS]: "Client Situation Records",
    [LocalStorageKeys.WALLET_RECORDS]: "Wallet Records",
    [LocalStorageKeys.WALLET_LIST]: "Wallet Directory",
    [LocalStorageKeys.EXPENSE_RECORDS]: "Expense Records",
    [LocalStorageKeys.VOUCHER_RECORDS]: "Voucher Records",
    [LocalStorageKeys.OFFER_RECORDS]: "Offer Records",
    [LocalStorageKeys.OFFER_TASKS]: "Pending Document Tasks",
    [LocalStorageKeys.AGENT_SIT_RECORDS]: "Agent Situation Records",

    [LocalStorageKeys.PAYMENT_RECORDS]: "Payment Records",
    [LocalStorageKeys.SUPPLY_PAYMENT_RECORDS]: "Supply Payment Records",
    [LocalStorageKeys.SUPPLY_PAYMENT_MODE_RECORDS]: "Supply Payments by Mode",
    [LocalStorageKeys.SUPPLY_PAYMENT_USER_RECORDS]: "Supply Payments by User",
    [LocalStorageKeys.SUPPLY_PAYMENT_DEPOT_RECORDS]: "Supply Payments by Depot",
    [LocalStorageKeys.PAYMENT_MODE_RECORDS]: "Payment Modes",
    [LocalStorageKeys.PAYMENT_TYPE_RECORDS]: "Payment Types",
    [LocalStorageKeys.PAYMENT_USER_RECORDS]: "Payments by User",
    [LocalStorageKeys.PAYMENT_DEPOT_RECORDS]: "Payments by Depot",

    [LocalStorageKeys.DEPOSIT_RECORDS]: "Deposit Records",
    [LocalStorageKeys.PENDING_DEPOSIT_RECORDS]: "Pending Deposit Records",
    [LocalStorageKeys.PENDING_DEPOSIT_ITEMS]: "Pending Deposit Items",

    [LocalStorageKeys.INVENTORY_BATCHED_RECORDS]: "Inventory Batched Records",
    [LocalStorageKeys.INVENTORY_TASKS]: "Pending Inventory Tasks",
    [LocalStorageKeys.CURRENT_INVENTORY]: "Current Inventory",
    [LocalStorageKeys.CURRENT_INVENTORY_VISUAL]: "Current Inventory (Visual)",

    // System keys
    sess_info: "Session Info",
    theme: "Display Theme",
    i18nextLng: "Language",
};

export const DATE_FILTER_OPTIONS = ['equals', 'notEquals', 'between', 'lessThan', 'lessThanOrEqualTo', 'greaterThan', 'greaterThanOrEqualTo', 'betweenInclusive'];
export const TEXT_FILTER_OPTIONS = ['fuzzy', 'contains', 'startsWith', 'endsWith', 'equals', 'notEquals'];
export const NUMBER_FILTER_OPTIONS = ['contains', 'startsWith', 'endsWith', 'equals', 'notEquals', 'between', 'betweenInclusive', 'lessThan', 'lessThanOrEqualTo', 'greaterThan', 'greaterThanOrEqualTo'];